import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken, AxiosError } from 'axios';

/**
 * Hand-written API client for the Organization Branding endpoints exposed by the
 * SG_tool_authenticator backend. It deliberately lives outside the NSwag-generated
 * file so that re-generating ApiServerHooks.ts does not drop it.
 *
 * Constructor signature matches `useGateApi<T>` expectations:
 *   new BrandingGateApiClient(baseUrl, axiosInstance)
 */
export interface IBrandingDTO {
    logoUrl?: string | null;
    logoMarkUrl?: string | null;
    faviconUrl?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    brandName?: string | null;
    urlSlug?: string | null;
}

export interface IGetTenantBrandingResponse {
    branding: IBrandingDTO;
}

export interface ISaveTenantBrandingCommand extends IBrandingDTO {}

export interface ISaveTenantBrandingResponse {
    ok: boolean;
    branding: IBrandingDTO;
}

export interface IResetTenantBrandingResponse {
    ok: boolean;
}

function isAxiosError(error: unknown): error is AxiosError {
    return (error as AxiosError)?.isAxiosError === true;
}

export class BrandingGateApiClient {
    protected instance: AxiosInstance;
    protected baseUrl: string;

    constructor(baseUrl?: string, instance?: AxiosInstance) {
        this.instance = instance ?? axios.create();
        this.baseUrl = baseUrl ?? "";
    }

    /**
     * Public (unauthenticated) lookup of a tenant's branding by its URL slug, used to render
     * the branded login page before the user signs in. Resolves to null when the slug does not
     * match any tenant (HTTP 404) so callers can fall back to the default branding.
     */
    getBrandingBySlug(slug: string, cancelToken?: CancelToken): Promise<IBrandingDTO | null> {
        const options: AxiosRequestConfig = {
            method: 'GET',
            url: this.baseUrl + '/Auth/branding-by-slug/' + encodeURIComponent(slug),
            headers: { Accept: 'application/json' },
            cancelToken,
        };
        return this.request<IBrandingDTO>(options)
            .catch((err: unknown) => {
                if ((err as Error & { status?: number })?.status === 404) {
                    return null;
                }
                throw err;
            });
    }

    getTenantBranding(cancelToken?: CancelToken): Promise<IGetTenantBrandingResponse> {
        const options: AxiosRequestConfig = {
            method: 'GET',
            url: this.baseUrl + '/Users/get-tenant-branding',
            headers: { Accept: 'application/json' },
            cancelToken,
        };
        return this.request<IGetTenantBrandingResponse>(options);
    }

    saveTenantBranding(body: ISaveTenantBrandingCommand, cancelToken?: CancelToken): Promise<ISaveTenantBrandingResponse> {
        const options: AxiosRequestConfig = {
            method: 'POST',
            url: this.baseUrl + '/Users/save-tenant-branding',
            data: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            cancelToken,
        };
        return this.request<ISaveTenantBrandingResponse>(options);
    }

    resetTenantBranding(cancelToken?: CancelToken): Promise<IResetTenantBrandingResponse> {
        const options: AxiosRequestConfig = {
            method: 'POST',
            url: this.baseUrl + '/Users/reset-tenant-branding',
            headers: { Accept: 'application/json' },
            cancelToken,
        };
        return this.request<IResetTenantBrandingResponse>(options);
    }

    private request<T>(options: AxiosRequestConfig): Promise<T> {
        return this.instance.request(options)
            .catch((err: unknown) => {
                if (isAxiosError(err) && err.response) {
                    return err.response;
                }
                throw err;
            })
            .then((response: AxiosResponse) => {
                const status = response.status;
                if (status === 200) {
                    return response.data as T;
                }
                if (status === 204) {
                    return undefined as unknown as T;
                }
                const data = response.data;
                // Prefer a structured { code, message } body. Fall back to a plain string body,
                // but never surface an HTML error page (e.g. the ASP.NET developer page) verbatim.
                const code: string | undefined = (data && typeof data === 'object') ? data.code : undefined;
                let message: string;
                if (data && typeof data === 'object' && typeof data.message === 'string') {
                    message = data.message;
                } else if (typeof data === 'string' && data.trim() !== '' && !/^\s*</.test(data)) {
                    message = data;
                } else {
                    message = `Request failed with status ${status}`;
                }
                const error = new Error(message) as Error & { status?: number; code?: string };
                error.status = status;
                error.code = code;
                throw error;
            });
    }
}
